/* eslint-disable no-param-reassign */
/* eslint-disable no-return-await */
/* eslint-disable no-throw-literal */
import RetailerOffer from '../../models/RetailerOffer';
import { getEnvironment } from '@andromeda/util-environment';
import {
	GetOffersResponse,
	GetLocationsResponse,
	GetLocationsResponseKr,
	GetLocationsResponseTgt,
	GetProductsResponse,
} from './response';
import { Chains } from '@andromeda/polaris-data-access-products';
import { MercuryStore, MercuryStoreKr, MercuryStoreTgt } from './location';

type Retailer = {
	squareUrl: string;
	rectanglerUrl: string;
	whiteSquareUrl: string;
	whiteRectangleUrl: string;
	gsId: number;
};

export enum MercuryRetailer {
	WALMART = 'wmt',
	KROGER = 'kro',
	AMAZON = 'amzn',
	TARGET = 'tgt',
	DRIZLY = 'drz',
	RESERVE = 'res',
}

export const integratedRetailerMap: { [key: string]: MercuryRetailer } = {
	walmart: MercuryRetailer.WALMART,
	kroger: MercuryRetailer.KROGER,
	target: MercuryRetailer.TARGET,
};

export const defaultRetailerLocationNames = {
	[MercuryRetailer.WALMART]: 'Walmart',
	[MercuryRetailer.TARGET]: 'Target',
	[MercuryRetailer.KROGER]: 'Kroger',
	[MercuryRetailer.AMAZON]: 'Amazon',
	[MercuryRetailer.DRIZLY]: 'Drizly',
	[MercuryRetailer.RESERVE]: 'Reserve',
};

export const retailerAffiliateConfigs = {
	[MercuryRetailer.WALMART]: {
		subId1: 'WMT',
		goToURI: 'https://goto.walmart.com/c/2375857/1066108/9383',
		affiliateURI: 'https://affil.walmart.com/cart/0/2',
	},
	[MercuryRetailer.TARGET]: {
		subId1: 'TGT',
		goToURI: 'https://goto.target.com/QO6PZM',
		affiliateURI: 'https://www.target.com/co-cart',
	},
};

export const isWalmartLocation = (
	location: MercuryStore | MercuryStoreKr | MercuryStoreTgt
): location is MercuryStore =>
	(location as MercuryStore).fulfillmentStoreId !== undefined;

export const isKrogerLocation = (
	location: MercuryStore | MercuryStoreKr | MercuryStoreTgt
): location is MercuryStoreKr =>
	(location as MercuryStoreKr).address.line1 !== undefined;

export const isTargetLocation = (
	location: MercuryStore | MercuryStoreKr | MercuryStoreTgt
): location is MercuryStoreTgt =>
	(location as MercuryStoreTgt).address.line !== undefined;

const apiUrls: { [name: string]: string } = {
	dev: 'https://dev-products.polaris.me/v1/integrations',
	qa: 'https://qa-products.polaris.me/v1/integrations',
	staging: 'https://staging-products.polaris.me/v1/integrations',
	prod: 'https://products.polaris.me/v1/integrations',
};

const apiUrl = apiUrls[getEnvironment(window.location)];

const fetchJson = async (url: string, payload: any): Promise<Object> => {
	const headers = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
	};
	const fetchOpts = {
		method: 'POST',
		headers,
		body: JSON.stringify(payload),
	};
	const response = await fetch(url, fetchOpts);
	// eslint-disable-next-line no-return-await
	return await response.json();
};

// eslint-disable-next-line consistent-return
const fetchRetry = async (
	url: string,
	payload: any,
	retries = 2
): Promise<Object> => {
	try {
		const headers = {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		};
		const fetchOpts = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		const response = await fetch(url, fetchOpts);
		return await response.json();
	} catch (error) {
		console.error(error);
		if (retries > 0) {
			// eslint-disable-next-line no-return-await
			return await fetchRetry(url, payload, retries - 1);
		}
		throw error as Error;
	}
};

export const getOffers = async (
	ingredientIds: number[],
	retailer: MercuryRetailer,
	locationId: string,
	isBundle: boolean,
	limit: number = 5
): Promise<[GetOffersResponse, Error | null]> => {
	const endpoint = isBundle
		? `${apiUrl}/${retailer}/bundle/offers`
		: `${apiUrl}/${retailer}/offers`;
	// const endpoint = `${apiUrl}/${retailer}/offers`;
	const payload = {
		ingredientIds,
		limit,
		locationId,
	};
	const payloadBundle = {
		ingredientIds,
		limit,
	};
	try {
		const data = isBundle
			? ((await fetchRetry(endpoint, payloadBundle)) as GetOffersResponse)
			: ((await fetchRetry(endpoint, payload)) as GetOffersResponse);
		return [data, null];
	} catch (ex) {
		return [{ accessPointId: '', offers: [] } as GetOffersResponse, ex];
	}
};

/**
 * Gets products from Walmart without pricing information
 * @param ingredientIds list of item Ids used to get products
 * @param limit number of products to return for each ingredientId
 */
export const getProducts = async (
	ingredientIds: number[],
	limit: number = 5
): Promise<[GetProductsResponse, Error | null]> => {
	const endpoint = `${apiUrl}/wmt/products`;
	const payload = {
		ingredientIds,
		limit,
	};
	try {
		const data = (await fetchJson(
			endpoint,
			payload
		)) as GetProductsResponse;
		return [data, null];
	} catch (err) {
		return [{ products: [] } as GetProductsResponse, err];
	}
};

export const getDefaultOffers = async (
	promotionId: string
): Promise<[GetOffersResponse, Error | null]> => {
	try {
		const endpoint = `https://cdn.polaris.me/cva/shopping-list/c/defaults/${promotionId}.json`;
		const response = await fetch(endpoint);
		const data = await response.json();
		return [data, null];
	} catch (err) {
		return [{ accessPointId: '', offers: [] } as GetOffersResponse, err];
	}
};

export const getDefaultOffersSc = async (
	ingredientIds: number[],
	retailer: string,
	limit: number = 1
): Promise<[GetProductsResponse, Error | null]> => {
	try {
		const endpoint = `https://products.polaris.me/v1/integrations/${retailer}/products`;
		const payload = {
			ingredientIds,
			limit,
		};
		const data = (await fetchJson(
			endpoint,
			payload
		)) as GetProductsResponse;
		return [data, null];
	} catch (err) {
		return [{ products: [] } as GetProductsResponse, err];
	}
};

export const getLocationsKr = async (
	zip: string,
	limit: number = 20
): Promise<[GetLocationsResponseKr, Error | null]> => {
	const endpoint =
		zip && zip.length > 0
			? `${apiUrl}/kro/locations?zip=${zip}`
			: `${apiUrl}/kro/locations`;
	const payload = {
		limit,
	};
	try {
		const data = (await fetchJson(
			endpoint,
			payload
		)) as GetLocationsResponseKr;

		const promiseArray: any[] = [];
		const GSIds = new Map<number, string>();

		/* eslint no-param-reassign: "error" */
		data.locations.forEach((l) => {
			const chain = Chains.get(l.chain);
			if (chain) {
				l.internalChainName = l.chain;
				l.chain = chain.Name;
				l.logo = chain.ImageUrl;
				l.url = chain.Url;
				l.preformatted = chain.Preformatted;
				if (chain.GSId && !GSIds.has(chain.GSId)) {
					GSIds.set(chain.GSId, '');
					promiseArray.push(
						fetchJson(
							'https://products.polaris.me/v1/integrations/retailer/search',
							{ gsIds: [chain.GSId] }
						)
					);
				}
			}
		});
		const retailerLogos = await Promise.all(promiseArray);
		retailerLogos.forEach((logo) => {
			if (logo.retailers.length > 0) {
				GSIds.set(logo.retailers[0].gsId, logo.retailers[0].squareUrl);
			}
		});
		data.locations.forEach((l) => {
			const chain = l.internalChainName
				? Chains.get(l.internalChainName)
				: '';
			if (chain && chain.GSId) {
				l.logo = GSIds.get(chain.GSId) ?? l.logo;
			}
		});
		return [data as GetLocationsResponseKr, null];
	} catch (err) {
		return [{ locations: [] } as GetLocationsResponseKr, err];
	}
};

export const getLocationsTgt = async (
	zip: string,
	limit: number = 20
): Promise<[GetLocationsResponseTgt, Error | null]> => {
	const endpoint =
		zip && zip.length > 0
			? `${apiUrl}/tgt/locations?zip=${zip}`
			: `${apiUrl}/tgt/locations`;
	const payload = {
		limit,
	};
	try {
		const data = (await fetchJson(
			endpoint,
			payload
		)) as GetLocationsResponseTgt;
		return [data, null];
	} catch (err) {
		return [{ locations: [] } as GetLocationsResponseTgt, err];
	}
};

export const getLocations = async (
	retailer: MercuryRetailer,
	zip: string = '',
	limit: number = 20
): Promise<
	[
		GetLocationsResponse | GetLocationsResponseKr | GetLocationsResponseTgt,
		Error | null
	]
> => {
	// ensure Zip is a string
	zip = zip.toString();
	if (retailer === MercuryRetailer.KROGER) {
		return getLocationsKr(zip, limit);
	}
	if (retailer === MercuryRetailer.TARGET) {
		return getLocationsTgt(zip, limit);
	}
	const endpoint =
		zip && zip.length > 0
			? `${apiUrl}/wmt/locations?zip=${zip}`
			: `${apiUrl}/wmt/locations`;
	console.log(`Checkout Zip ${zip}`);
	const payload = {
		limit,
	};
	try {
		const data = (await fetchJson(
			endpoint,
			payload
		)) as GetLocationsResponse;
		return [data, null];
	} catch (err) {
		return [{ locations: [] } as GetLocationsResponse, err];
	}
};

const getQueryStringParamValue = (key: string): string => {
	const url = new URL(window.location.href);
	return url.searchParams.get(key) || '';
};

// eslint-disable-next-line max-len
export const fetchRetailerOffers = async (
	itemIds: number[],
	fulfillmentStoreId: string,
	isBundle: boolean,
	maxOffers: number = 5,
	retailerName = MercuryRetailer.WALMART
): Promise<RetailerOffer[] | undefined> => {
	const limitp = getQueryStringParamValue('limit');
	const limit = parseInt(limitp, 10) || maxOffers;
	// eslint-disable-next-line no-restricted-globals
	const offerResult: [GetOffersResponse, Error | null] = isNaN(limit)
		? // eslint-disable-next-line max-len
		  await getOffers(itemIds, retailerName, fulfillmentStoreId, isBundle)
		: // eslint-disable-next-line max-len
		  await getOffers(
				itemIds,
				retailerName,
				fulfillmentStoreId,
				isBundle,
				limit
		  );
	if (offerResult[1] != null) {
		// eslint-disable-next-line no-console
		console.error(offerResult[1]);
		return [];
	}
	return offerResult[0].offers.map(RetailerOffer.FromMercuryOffer);
};

// // eslint-disable-next-line max-len
export const fetchDefaultRetailerOffers = async (
	promotionId: string
): Promise<RetailerOffer[] | undefined> => {
	const limitp = getQueryStringParamValue('limit');
	const limit = parseInt(limitp, 10);
	const retailerName = MercuryRetailer.WALMART;
	// eslint-disable-next-line no-restricted-globals
	const offerResult: [GetOffersResponse, Error | null] = isNaN(limit)
		? // eslint-disable-next-line max-len
		  await getDefaultOffers(promotionId)
		: // eslint-disable-next-line max-len
		  await getDefaultOffers(promotionId);
	if (offerResult[1] != null) {
		// eslint-disable-next-line no-console
		console.error(offerResult[1]);
		return [];
	}
	return offerResult[0].offers.map(RetailerOffer.FromMercuryOffer);
};

export const fetchRetailerLogo = async (
	gsId: number
): Promise<Retailer | null> => {
	const response: any = await fetchJson(
		'https://products.polaris.me/v1/integrations/retailer/search',
		{
			gsIds: [gsId],
		}
	);
	return response && response.retailers ? response.retailers[0] : null;
};
